import { onKeyStroke } from '@vueuse/core';
import { Feature } from 'ol';
import type { Coordinate } from 'ol/coordinate';
import type { Geometry } from 'ol/geom';
import { Point } from 'ol/geom';
import type Polygon from 'ol/geom/Polygon';
import { fromExtent } from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { defineStore } from 'pinia';
import { computed, shallowRef } from 'vue';

import { isNonEmptyString } from '@nora-monorepo/shared';
import {
  appLogger,
  isWebViewMode,
  MapLoggingContext,
} from '@nora-monorepo/ui-shared';

import { createPastedFeature } from '#nora-ui/map/draw-feature-selection/draw-feature-selection-utils';
import { selectionBoundingBoxStyle } from '#nora-ui/map/drawtools/styles/select-style';
import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import { useMapStore } from '#nora-ui/map/stores/map-store';

interface DrawFeatureSelectionProperties {
  drawLayerId?: string;
  lineWidth?: number;
  radius?: number;
}

/**
 * Value to indicate how far away from the original feature the duplicated version should appear (in pixels)
 */
const DUPLICATE_OFFSET_PX = 20;

function createDrawFeatureSelectionStore(mapId: string) {
  return () => {
    const selectedFeature = shallowRef<Feature<Geometry> | null>(null);
    const candidateFeatures = shallowRef<Feature<Geometry>[]>([]);
    const clipboardFeature = shallowRef<Feature<Geometry> | null>(null);
    const lastPointerCoordinate = shallowRef<Coordinate | null>(null);
    let requestDeleteConfirmation: (() => void) | null = null;
    let selectedFeatureChangeListener: (() => void) | null = null;
    let selectedFeatureChangeListenerFeature: Feature<Geometry> | null = null;

    const selectionVectorSource = new VectorSource();
    const selectionVectorLayer = new VectorLayer<VectorSource>({
      source: selectionVectorSource,
      style: selectionBoundingBoxStyle,
      zIndex: 1000,
    });

    const mapStore = useMapStore();
    const map = mapStore.getMapByIdOrThrow(mapId);
    const drawToolUI = map.drawToolUI;
    const drawToolManager = map.drawToolManager;
    const isCandidateSelectionOpen = computed(() => candidateFeatures.value.length > 1);

    function createBoundingBoxFeature(feature: Feature<Geometry>): Feature<Polygon> | null {
      const geometry = feature.getGeometry();
      if (!geometry) {
        return null;
      }

      const [minX, minY, maxX, maxY] = geometry.getExtent();
      if (
        !Number.isFinite(minX)
        || !Number.isFinite(minY)
        || !Number.isFinite(maxX)
        || !Number.isFinite(maxY)
      ) {
        return null;
      }

      const {
        lineWidth,
        radius,
      } = getFeatureProperties<DrawFeatureSelectionProperties>(feature);
      const resolution = drawToolManager.map.getOlMapOrThrow().getView().getResolution() ?? 1;
      const lineWidthBuffer = typeof lineWidth === 'number'
        ? (lineWidth / 2) * resolution
        : 0;
      const circleRadius = geometry instanceof Point && typeof radius === 'number'
        ? radius
        : 0;
      const polygon = fromExtent([
        minX - circleRadius - lineWidthBuffer,
        minY - circleRadius - lineWidthBuffer,
        maxX + circleRadius + lineWidthBuffer,
        maxY + circleRadius + lineWidthBuffer,
      ]);

      const boundingBoxFeature = new Feature(polygon);
      boundingBoxFeature.setId(`selection-bbox-${feature.getId()}`);

      return boundingBoxFeature;
    }

    function showSelectionBox(feature: Feature<Geometry>): void {
      const olMap = drawToolManager.map.getOlMapOrThrow();
      const layers = olMap.getLayers().getArray();

      if (!layers.includes(selectionVectorLayer)) {
        olMap.addLayer(selectionVectorLayer);
      }

      selectionVectorSource.clear();

      const boundingBoxFeature = createBoundingBoxFeature(feature);
      if (boundingBoxFeature) {
        selectionVectorSource.addFeature(boundingBoxFeature);
      }
    }

    function listenForSelectedFeatureChanges(feature: Feature<Geometry>): void {
      selectedFeatureChangeListener = () => {
        if (selectedFeature.value === feature) {
          showSelectionBox(feature);
        }
      };

      feature.on('change', selectedFeatureChangeListener);
      selectedFeatureChangeListenerFeature = feature;
    }

    function stopListeningForSelectedFeatureChanges(): void {
      if (!selectedFeatureChangeListenerFeature || !selectedFeatureChangeListener) {
        return;
      }

      selectedFeatureChangeListenerFeature.un('change', selectedFeatureChangeListener);
      selectedFeatureChangeListener = null;
      selectedFeatureChangeListenerFeature = null;
    }

    function hideSelectionBox(): void {
      selectionVectorSource.clear();
    }

    function getDrawLayerId(feature: Feature<Geometry>): string | undefined {
      const { drawLayerId } = getFeatureProperties<DrawFeatureSelectionProperties>(feature);

      return isNonEmptyString(drawLayerId) ? drawLayerId : undefined;
    }

    function selectFeature(feature: Feature<Geometry>): void {
      if (selectedFeature.value) {
        deselectFeature();
      }

      candidateFeatures.value = [];
      selectedFeature.value = feature;

      listenForSelectedFeatureChanges(feature);
      showSelectionBox(feature);
    }

    function selectCandidate(feature: Feature<Geometry>): void {
      selectFeature(feature);
    }

    function openCandidateSelection(features: Feature<Geometry>[]): void {
      candidateFeatures.value = features;
    }

    function clearCandidateSelection(): void {
      candidateFeatures.value = [];
    }

    function deselectFeature(): void {
      stopListeningForSelectedFeatureChanges();
      hideSelectionBox();
      selectedFeature.value = null;
    }

    function reset(): void {
      deselectFeature();
      clearCandidateSelection();
      clipboardFeature.value = null;

      const olMap = drawToolManager.map.getOlMap();
      if (!olMap) {
        return;
      }

      const layers = olMap.getLayers().getArray();
      if (layers.includes(selectionVectorLayer)) {
        olMap.removeLayer(selectionVectorLayer);
      }
    }

    function isKeyboardInteractionEnabled(): boolean {
      return drawToolUI.activeDrawTool.value === null;
    }

    async function duplicateSelectedFeature(): Promise<void> {
      copy();
      const resolution = map.getOlMapOrThrow().getView().getResolution() ?? 1;
      await pasteFeature([
        DUPLICATE_OFFSET_PX * resolution,
        -DUPLICATE_OFFSET_PX * resolution,
      ]);
      reset();
    }

    async function removeSelectedFeature(): Promise<void> {
      const feature = selectedFeature.value;
      if (!feature) {
        return;
      }

      const drawLayerId = getDrawLayerId(feature);
      if (drawLayerId === undefined) {
        throw new Error('Selected feature does not belong to a draw layer');
      }

      drawToolManager.addToUndoStack({
        type: 'remove',
        features: [feature],
      });

      await drawToolManager.removeFeature(feature, drawLayerId);
      deselectFeature();
      clearCandidateSelection();
    }

    async function deleteSelectedFeature(): Promise<void> {
      await removeSelectedFeature();
      reset();
    }

    function copy(): void {
      const feature = selectedFeature.value;
      if (!feature) {
        return;
      }

      clipboardFeature.value = feature.clone();
      clipboardFeature.value.setId(undefined);
      clipboardFeature.value.setProperties({
        id: undefined,
      });
    }

    async function cut(): Promise<void> {
      copy();
      await removeSelectedFeature();
    }

    async function pasteFeature(offset?: Coordinate): Promise<void> {
      const clipboard = clipboardFeature.value;
      if (!clipboard) {
        return;
      }

      const fallbackCoordinate = map.getOlMapOrThrow().getView().getCenter() ?? undefined;
      const targetCoordinate = offset === undefined
        ? (!isWebViewMode()
            ? lastPointerCoordinate.value ?? fallbackCoordinate
            : fallbackCoordinate)
        : undefined;
      const feature = createPastedFeature(clipboard, targetCoordinate, offset);

      const savedFeature = await drawToolManager.saveFeature(feature);
      drawToolManager.addToUndoStack({
        type: 'add',
        features: [savedFeature],
      });

      drawToolManager.vectorSource.addFeature(savedFeature);
      selectFeature(savedFeature);
    }

    async function paste(): Promise<void> {
      await pasteFeature();
    }

    function setLastPointerCoordinate(coordinate: Coordinate): void {
      lastPointerCoordinate.value = coordinate;
    }

    function registerDeleteConfirmationHandler(handler: () => void): () => void {
      requestDeleteConfirmation = handler;

      return () => {
        if (requestDeleteConfirmation === handler) {
          requestDeleteConfirmation = null;
        }
      };
    }

    const stopKeyboardShortcutListener = onKeyStroke(true, (event) => {
      if (!isKeyboardInteractionEnabled()) {
        return;
      }

      if (event.key === 'Escape' || event.key === 'Enter') {
        deselectFeature();
        return;
      }

      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedFeature.value) {
          requestDeleteConfirmation?.();
        }
        return;
      }

      if (!event.metaKey) {
        return;
      }

      if (event.key === 'c') {
        copy();
        return;
      }

      if (event.key === 'v') {
        void paste().catch((error) => {
          appLogger.error({
            message: 'Could not paste selected draw feature',
            context: {
              map: new MapLoggingContext({ mapId }),
            },
            sensitiveContext: { error },
          });
        });
        return;
      }

      if (event.key === 'x') {
        void cut().catch((error) => {
          appLogger.error({
            message: 'Could not cut selected draw feature',
            context: {
              map: new MapLoggingContext({ mapId }),
              featureId: selectedFeature.value?.getId(),
            },
            sensitiveContext: { error },
          });
        });
      }
    });

    function cleanup(): void {
      reset();
      stopKeyboardShortcutListener();
    }

    return {
      candidateFeatures,
      clearCandidateSelection,
      cleanup,
      copy,
      cut,
      deleteSelectedFeature,
      deselectFeature,
      duplicateSelectedFeature,
      isCandidateSelectionOpen,
      openCandidateSelection,
      paste,
      registerDeleteConfirmationHandler,
      reset,
      selectCandidate,
      setLastPointerCoordinate,
      selectedFeature,
      selectionVectorLayer,
    };
  };
}

export function useDrawFeatureSelectionStore(mapId: string) {
  return defineStore(
    `drawFeatureSelectionStore-${mapId}`,
    createDrawFeatureSelectionStore(mapId),
  )();
}
